import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarClock, X, Link2, Share2 } from "lucide-react";
import { shareLiveEvent, copyText, countdown } from "./modules/LiveEvents";

const API = process.env.REACT_APP_BACKEND_URL;

// Global scheduled-live preview — a slim pill on every page announcing the next public live event,
// with a tap-to-preview modal carrying the keyed watch link. Mobile-first, session-dismissible.
export const UpcomingLiveBanner = () => {
  const [next, setNext] = useState(null);
  const [dismissed, setDismissed] = useState(null);
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);
  // Masters choose whether the global upcoming-live banner shows at all (Master Control → Site Content)
  const [allowed, setAllowed] = useState(true);
  useEffect(() => {
    fetch(`${API}/api/site/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => setAllowed(!s || (s.live_preview_pages || []).includes("banner")))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const load = () =>
      fetch(`${API}/api/live/upcoming`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((d) => {
          const pub = (Array.isArray(d) ? d : []).filter((e) => e.is_public);
          setNext(pub[0] || null);
        })
        .catch((err) => console.debug("upcoming banner failed", err));
    load();
    const iv = setInterval(load, 30000);
    const tick = setInterval(() => setTick((t) => t + 1), 30000);
    return () => {
      clearInterval(iv);
      clearInterval(tick);
    };
  }, []);

  if (!allowed || !next || dismissed === next.id) return null;

  const when = new Date(next.scheduled_at).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-[calc(100vw-1.5rem)] animate-fade-in"
        data-testid="upcoming-live-banner"
      >
        <div className="flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 bg-white/95 backdrop-blur border border-[#B3E0F2] rounded-full shadow-xl dark:bg-[#273244]/95 dark:border-[#374457]">
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-[#5CB3DC] animate-pulse" />
            <span className="text-[9px] font-extrabold uppercase tracking-wider text-[#3D7E9E]">Upcoming</span>
          </span>
          <span className="text-[11px] font-bold text-[#374151] dark:text-slate-200 truncate max-w-[120px] sm:max-w-[200px]">{next.title}</span>
          <span className="text-[10px] font-extrabold text-[#5CB3DC] shrink-0" data-testid="upcoming-live-countdown">{countdown(next.scheduled_at)}</span>
          <button
            onClick={() => setOpen(true)}
            className="shrink-0 px-3.5 py-1.5 bg-[#5CB3DC] hover:bg-[#4AA3D0] text-white text-[11px] font-extrabold rounded-full transition-colors"
            data-testid="upcoming-live-preview-btn"
          >
            Preview
          </button>
          <button
            onClick={() => setDismissed(next.id)}
            className="shrink-0 p-1.5 text-[#9CA3AF] hover:text-[#374151] rounded-full transition-colors"
            title="Hide until the next event"
            data-testid="upcoming-live-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {open &&
        createPortal(
          <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="upcoming-live-modal">
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-white rounded-3xl border-2 border-[#B3E0F2] shadow-2xl overflow-hidden dark:bg-[#273244] dark:border-[#374457]"
            >
              <div className="relative h-44 bg-gradient-to-br from-[#E6F2FA] via-[#FFF9F2] to-[#FCE6E9]">
                {next.poster ? (
                  <img src={next.poster} alt={next.title} className="w-full h-full object-cover" data-testid="upcoming-live-poster" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <CalendarClock className="w-12 h-12 text-[#5CB3DC]" />
                  </div>
                )}
                <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-[#5CB3DC] text-white text-[10px] font-extrabold uppercase rounded-full shadow">
                  <CalendarClock className="w-3 h-3" /> Coming up · {countdown(next.scheduled_at)}
                </span>
                <button
                  onClick={() => setOpen(false)}
                  className="absolute top-3 right-3 p-1.5 bg-white/90 rounded-full text-[#6B7280] hover:text-[#374151] shadow"
                  data-testid="upcoming-live-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#3D7E9E]">{next.host_name} is going live on NestTrack</p>
                <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100 mt-1">{next.title}</h3>
                {next.description && <p className="text-xs text-[#6B7280] dark:text-slate-300 mt-1.5 leading-relaxed">{next.description}</p>}
                <p className="text-[11px] text-[#9CA3AF] font-bold mt-2.5">
                  {next.facilityName || "NestTrack"} · {when}
                </p>
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => copyText(next.join_link || `${window.location.origin}/?live=${next.id}`, "Watch link copied — it joins the stream instantly when it starts.")}
                    className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#5CB3DC] hover:bg-[#4AA3D0] text-white text-xs font-extrabold rounded-full transition-colors"
                    data-testid="upcoming-live-copy-link"
                  >
                    <Link2 className="w-3.5 h-3.5" /> Copy Watch Link
                  </button>
                  <button
                    onClick={() => shareLiveEvent(next)}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-xs font-extrabold rounded-full transition-colors dark:bg-[#273244]"
                    data-testid="upcoming-live-share"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                </div>
                <p className="text-[10px] text-[#9CA3AF] font-semibold text-center mt-3">Everyone is notified the moment it starts — the same link goes live.</p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default UpcomingLiveBanner;
